import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Repeat, TrendingUp, Users, DollarSign, Calendar, Pause, X as XIcon, Sparkles } from "lucide-react";

function daysAgoISO(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function AdminSubAnalytics() {
    const [fromDate, setFromDate] = useState(daysAgoISO(89));
    const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async (f = fromDate, t = toDate) => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/subscriptions/analytics", { params: { from_date: f, to_date: t } });
            setData(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const setRange = (days) => {
        const f = daysAgoISO(days - 1), t = new Date().toISOString().slice(0, 10);
        setFromDate(f); setToDate(t); load(f, t);
    };

    const maxWeekly = Math.max(1, ...(data?.weekly_new || []).map(w => w.new_subs || 0));
    const maxProd = Math.max(1, ...(data?.top_products || []).map(p => p.subscribers || 0));
    const maxCadence = Math.max(1, ...(data?.cadence_mix || []).map(c => c.count || 0));

    return (
        <div className="space-y-5" data-testid="sub-analytics-page">
            <div>
                <div className="label-eyebrow text-sky-700">Customer success</div>
                <h1 className="font-serif text-4xl text-stone-900 inline-flex items-center gap-3"><Repeat className="w-7 h-7 text-sky-700" />Subscription analytics</h1>
                <p className="text-sm text-stone-500 mt-1">Recurring revenue, churn, lifetime value and the most loved auto-refill remedies.</p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-end gap-3 flex-wrap bg-white border border-stone-200 p-4">
                <div><label className="text-[10px] uppercase tracking-widest text-stone-500">From</label><Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="sa-from" /></div>
                <div><label className="text-[10px] uppercase tracking-widest text-stone-500">To</label><Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-none border-stone-300 mt-1" data-testid="sa-to" /></div>
                <Button type="submit" disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50">Apply</Button>
                <div className="flex gap-1 ml-auto">
                    <button type="button" onClick={() => setRange(30)} className="text-xs px-2 py-1 border border-stone-300 hover:bg-stone-50">30 days</button>
                    <button type="button" onClick={() => setRange(90)} className="text-xs px-2 py-1 border border-stone-300 hover:bg-stone-50">90 days</button>
                    <button type="button" onClick={() => setRange(365)} className="text-xs px-2 py-1 border border-stone-300 hover:bg-stone-50">12 months</button>
                </div>
            </form>

            {data && (
                <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Kpi big label="MRR" v={formatINR(data.totals.mrr)} sub={`ARR ${formatINR(data.totals.arr)}`} icon={TrendingUp} testid="kpi-mrr" />
                        <Kpi big label="Active subscribers" v={data.totals.active} sub={`${data.totals.due_today} due today`} icon={Users} testid="kpi-active" />
                        <Kpi big label="Avg LTV / subscriber" v={formatINR(data.totals.avg_ltv)} sub="Lifetime revenue" icon={DollarSign} testid="kpi-ltv" />
                        <Kpi big label="Churn rate" v={`${data.totals.churn_pct}%`} sub={`${data.totals.cancelled} cancellations all-time`} icon={XIcon} testid="kpi-churn" />
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Kpi label="Paused" v={data.totals.paused} icon={Pause} />
                        <Kpi label="New subs (range)" v={data.totals.new_subs_in_range} />
                        <Kpi label="Refill orders (range)" v={data.totals.refill_orders_in_range} />
                        <Kpi label="Refill revenue (range)" v={formatINR(data.totals.refill_revenue_in_range)} />
                    </div>

                    <div className="grid lg:grid-cols-2 gap-4">
                        <Card title="Top remedies on auto-refill">
                            {(data.top_products || []).length === 0 ? <Empty>No subscribers yet — they will appear here as customers sign up.</Empty> : (
                                <div className="space-y-2">
                                    {data.top_products.map(p => (
                                        <div key={p.product_id} className="flex items-center gap-3 text-xs">
                                            <Link to={`/product/${p.slug}`} className="w-40 text-stone-900 hover:text-sky-700 truncate" data-testid={`top-prod-${p.product_id}`}>{p.name}</Link>
                                            <div className="flex-1 h-4 bg-stone-100"><div className="h-full bg-sky-400" style={{ width: `${(p.subscribers / maxProd) * 100}%` }} /></div>
                                            <span className="w-12 text-right font-mono">{p.subscribers}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>

                        <Card title="Cadence mix (active subscriptions)">
                            {(data.cadence_mix || []).length === 0 ? <Empty>No active subscriptions to show.</Empty> : (
                                <div className="space-y-2">
                                    {data.cadence_mix.map(c => (
                                        <div key={c.cadence} className="flex items-center gap-3 text-xs">
                                            <span className="w-32 text-stone-700">{c.label}</span>
                                            <div className="flex-1 h-4 bg-stone-100"><div className="h-full bg-emerald-400" style={{ width: `${(c.count / maxCadence) * 100}%` }} /></div>
                                            <span className="w-12 text-right font-mono">{c.count}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>

                    <Card title="New subscriptions per week">
                        {(data.weekly_new || []).length === 0 ? <Empty>No new subscriptions in this window.</Empty> : (
                            <div className="space-y-2">
                                {data.weekly_new.map(w => (
                                    <div key={w.week_start} className="grid grid-cols-12 gap-2 items-center text-xs">
                                        <span className="col-span-2 font-mono text-stone-500">{w.week_start}</span>
                                        <div className="col-span-9 h-4 bg-stone-100"><div className="h-full bg-purple-400" style={{ width: `${(w.new_subs / maxWeekly) * 100}%` }} /></div>
                                        <span className="col-span-1 text-right font-mono">{w.new_subs}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 p-5">
                        <div className="flex items-start gap-3">
                            <Sparkles className="w-5 h-5 text-purple-700 mt-0.5" />
                            <div>
                                <div className="font-serif text-lg text-stone-900">Want to grow auto-refills?</div>
                                <p className="text-xs text-stone-600 mt-1">Use the <Link to="/admin/marketing" className="text-sky-700 underline">AI Campaign Launcher</Link> to draft a one-prompt campaign targeting your top-product subscribers. Tip: dormant customers respond well to a 20% relaunch offer.</p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function Kpi({ label, v, sub, icon: Icon, big = false, testid }) {
    return (
        <div className={`bg-white border ${big ? "border-sky-100" : "border-stone-200"} p-4`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="label-eyebrow text-stone-500">{label}</div>
                {Icon && <Icon className="w-4 h-4 text-sky-700" />}
            </div>
            <div className={`font-serif ${big ? "text-3xl" : "text-xl"} text-stone-900 mt-1`}>{v}</div>
            {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
        </div>
    );
}
function Card({ title, children }) {
    return <div className="bg-white border border-stone-200 p-5"><h2 className="font-serif text-lg text-stone-900 mb-3">{title}</h2>{children}</div>;
}
function Empty({ children }) { return <div className="text-xs text-stone-500 py-4 text-center"><Calendar className="w-6 h-6 mx-auto text-stone-300 mb-1" />{children}</div>; }
